import { createSignal, onCleanup, onMount, For, Show } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { useNavigate } from '@solidjs/router';
import { useApi, isOk, type SeedCrackWorldDto } from '../api';
import { useTranslation } from '../hooks/useTranslation';
import Text from '../components/Text';
import { formatDateTime } from '../services/dateFormat';
import {
    SEED_CRACK_POLL_MS,
    SEED_CRACK_STATUS_CRACKED,
    seedCrackStatusLabelKey,
    seedCrackStatusIcon,
    dimensionLabel,
    formatCandidates,
    formatEta,
} from '../services/seedCrack';
import styles from './SeedCracker.module.css';

function statusClass(status: number): string {
    switch (status) {
        case 1: return styles.statusFeasible;
        case 2: return styles.statusCracking;
        case 3: return styles.statusCracked;
        case 4: return styles.statusAmbiguous;
        default: return styles.statusCollecting;
    }
}

function sortWorlds(worlds: SeedCrackWorldDto[]): SeedCrackWorldDto[] {
    return [...worlds].sort((a, b) => {
        const aCracked = a.status === SEED_CRACK_STATUS_CRACKED ? 0 : 1;
        const bCracked = b.status === SEED_CRACK_STATUS_CRACKED ? 0 : 1;
        if (aCracked !== bCracked) return aCracked - bCracked;
        return a.estimatedCandidates - b.estimatedCandidates;
    });
}

export default function SeedCracker() {
    const api = useApi();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [worlds, setWorlds] = createStore<SeedCrackWorldDto[]>([]);
    const [loading, setLoading] = createSignal(true);

    const fetchWorlds = async (silent = false) => {
        if (!silent) setLoading(true);
        const res = await api.seedCrack.getWorlds();
        if (isOk(res.data)) {
            setWorlds(reconcile(sortWorlds(res.data.value.worlds ?? []), { key: 'key' }));
        }
        if (!silent) setLoading(false);
    };

    onMount(async () => {
        await fetchWorlds();
        const handle = window.setInterval(() => {
            if (document.visibilityState === 'visible') void fetchWorlds(true);
        }, SEED_CRACK_POLL_MS);
        onCleanup(() => clearInterval(handle));
    });

    const openWorld = (key: string) => navigate(`/seed-cracker/world?key=${encodeURIComponent(key)}`);

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><i class="fa-solid fa-seedling" style={{ 'margin-right': 'var(--spacing-md)' }} /><Text val="SEED_CRACK_TITLE" /></h1>
                <Show when={!loading()}>
                    <span class={styles.entryCount}>{worlds.length} <Text val="SEED_CRACK_WORLDS" /></span>
                </Show>
            </div>

            <Show when={!loading()} fallback={
                <div class={styles.list}>
                    <div class={styles.skeletonCard} />
                    <div class={styles.skeletonCard} />
                    <div class={styles.skeletonCard} />
                </div>
            }>
                <Show when={worlds.length > 0} fallback={
                    <div class={styles.empty}>
                        <i class="fa-solid fa-seedling" />
                        <p><Text val="SEED_CRACK_EMPTY" /></p>
                    </div>
                }>
                    <div class={styles.list}>
                        <For each={worlds}>{(world) => (
                            <div
                                class={`fcard ${styles.card} ${styles.clickable}`}
                                role="button"
                                tabindex={0}
                                onClick={() => openWorld(world.key)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openWorld(world.key); } }}
                            >
                                <div class={styles.cardHeader}>
                                    <div class={styles.titleBlock}>
                                        <div class={styles.titleRow}>
                                            <span class={styles.cityName}>{world.citybuild || t('SEED_CRACK_CB_UNKNOWN')}</span>
                                            <span class={styles.dimensionBadge}>
                                                <i class="fa-solid fa-earth-europe" />{dimensionLabel(t, world.dimension)}
                                            </span>
                                            <Show when={world.isGlitch}>
                                                <span class={styles.glitchBadge}>
                                                    <i class="fa-solid fa-bug" /><Text val="SEED_CRACK_GLITCH" />
                                                </span>
                                            </Show>
                                        </div>
                                        <div class={styles.subtitle}>
                                            <span class={styles.serverAddress}><i class="fa-solid fa-network-wired" /> {world.serverAddress}</span>
                                            <span><Text val="SEED_CRACK_CENTER" /> <code>{world.centerX} / {world.centerZ}</code></span>
                                            <span><Text val="SEED_CRACK_DIAMETER" /> <code>{world.diameter}</code></span>
                                        </div>
                                    </div>
                                    <span class={`${styles.statusBadge} ${statusClass(world.status)}`}>
                                        <i class={seedCrackStatusIcon(world.status)} />
                                        <Text val={seedCrackStatusLabelKey(world.status)} />
                                    </span>
                                </div>

                                <div class={styles.metrics}>
                                    <div class={styles.metric}>
                                        <span class={styles.metricLabel}><Text val="SEED_CRACK_DUNGEONS" /></span>
                                        <span class={styles.metricValue}>{world.dungeonCount.toLocaleString()}</span>
                                    </div>
                                    <div class={styles.metric}>
                                        <span class={styles.metricLabel}><Text val="SEED_CRACK_BIOMES" /></span>
                                        <span class={styles.metricValue}>{world.biomeCount.toLocaleString()}</span>
                                    </div>
                                    <Show when={world.fortressCount > 0}>
                                        <div class={styles.metric}>
                                            <span class={styles.metricLabel}><Text val="SEED_CRACK_FORTRESSES" /></span>
                                            <span class={styles.metricValue}>{world.fortressCount.toLocaleString()}</span>
                                        </div>
                                    </Show>
                                    <div class={styles.metric}>
                                        <span class={styles.metricLabel}><Text val="SEED_CRACK_CONTRIBUTORS" /></span>
                                        <span class={styles.metricValue}>{world.contributorCount.toLocaleString()}</span>
                                    </div>
                                </div>

                                <div class={styles.footer}>
                                    <Show when={world.status === SEED_CRACK_STATUS_CRACKED && world.crackedSeedText != null} fallback={
                                        <div class={styles.outcome}>
                                            <span class={styles.outcomeLabel}><Text val="SEED_CRACK_CANDIDATES" /></span>
                                            <div class={styles.candidatesRow}>
                                                <span class={styles.candidatesValue}>{formatCandidates(world.estimatedCandidates)}</span>
                                                <span class={styles.eta}><Text val="SEED_CRACK_ETA" />: {formatEta(t, world.etaSeconds)}</span>
                                            </div>
                                        </div>
                                    }>
                                        <div class={styles.outcome}>
                                            <span class={styles.outcomeLabel}><Text val="SEED_CRACK_SEED" /></span>
                                            <span class={styles.seedValue}>{world.crackedSeedText}</span>
                                        </div>
                                    </Show>
                                    <span class={styles.updated}>
                                        <Text val="SEED_CRACK_UPDATED" />: {formatDateTime(world.updatedAt)}
                                    </span>
                                </div>
                            </div>
                        )}</For>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
